'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';
import styles from '../styles/Roadmap.module.css';

// Roadmap data structure with completion status
const roadmapData = [
  {
    id: 'phase-1',
    title: 'Launch of GameMax (Concept To Launch)',
    status: 'completed', // completed, in-progress, or upcoming
    completionPercentage: 100,
    items: [
      {
        title: 'Tech Stack Development, MVP App Launch & Tap Game Release',
        description: "Launched proprietary tech stack blending Web2/Web3, powering AI agents, loyalty systems, and payments on Base, Solana, Ethereum, and Polygon. Released MVP app with Play-to-Connect features for community engagement. Launched Tap Game with 60,000+ active users to grow the ecosystem."
      },
      {
        title: 'AI Coaching Services',
        description: 'Rolled out AI-powered coaching with virtual training from top pickleball players.'
      },
      {
        title: 'Ecommerce Integration',
        description: 'Integrated ecommerce tools, tracking purchases, metrics, and user engagement.'
      },
      {
        title: 'Community Building',
        description: 'Focused on engagement via social campaigns and partnerships in the pickleball community.'
      }
    ],
    icon: '/assets/Gamemax Icons launch.png'
  },
  {
    id: 'phase-2',
    title: 'Entering the World Stage',
    status: 'in-progress',
    completionPercentage: 75,
    items: [
      {
        title: 'Token Creation',
        description: 'Issuing the GAM utility token on Base, enabling seamless play-to-earn rewards and governance for our ecosystem.'
      },
      {
        title: 'Audit',
        description: 'Completing a comprehensive security audit with Certik, ensuring trust and transparency for our community.'
      },
      {
        title: 'Marketing Push',
        description: 'Launching a robust marketing push, leveraging AI agents to engage 726M racket sports players globally, driving adoption through viral campaigns, interactive challenges, and community events on Telegram and X.'
      }
    ],
    icon: '/assets/Gamemax Icons Global.png'
  },
  {
    id: 'phase-3',
    title: 'Growing the Family',
    status: 'upcoming',
    completionPercentage: 0,
    items: [
      {
        title: 'Community Engagement',
        description: "Building on early success, we'll keep the GameMax community buzzing with global tournaments, play-to-earn challenges, and social campaigns to boost engagement."
      },
      {
        title: 'Partnerships',
        description: "The GameMax team will forge strategic partnerships with racket sports organizations and blockchain platforms to expand reach and enhance GAM's value."
      },
      {
        title: 'Technology',
        description: "We'll enhance our AI coaching algorithms, improving personalized training and analytics for a seamless player experience."
      },
      {
        title: 'Exchange Listings',
        description: "GAM will be listed on top exchanges, increasing liquidity and accessibility for our growing community."
      }
    ],
    icon: '/assets/Gamemax Icons Growing.png'
  },
  {
    id: 'phase-4',
    title: 'Going Worldwide',
    status: 'upcoming',
    completionPercentage: 0,
    items: [
      {
        title: 'Enhanced Features',
        description: "We'll keep innovating by adding new features to the GameMax app, adapting to player needs and enhancing gameplay, training, and community tools."
      },
      {
        title: 'Global Outreach',
        description: "We'll promote GameMax globally, targeting 726M racket sports players across tennis, badminton, and table tennis, with secure transactions on Base."
      },
      {
        title: 'Continuous Improvement',
        description: "We're building an inclusive ecosystem, ensuring players of all levels can engage, earn, and connect through GAM's play-to-earn rewards."
      }
    ],
    icon: '/assets/Gamemax Icons Globe.png'
  },
  {
    id: 'phase-5',
    title: 'Evolution',
    status: 'upcoming',
    completionPercentage: 0,
    items: [
      {
        title: 'Badminton Integration',
        description: 'As the second racket sport, Badminton will be added to the platform in Q4 2025. This sport will be fully integrated with tailored points (e.g., BIRDIES) and gameplay mechanics, ensuring a distinct experience for users who select Badminton as their sport.'
      },
      {
        title: 'Monetization for All Sports',
        description: 'Continued evolution of the Tap Game with additional monetization features for each newly added racket sport.'
      },
      {
        title: 'Quarterly Releases of New Racket Sports',
        description: 'Each quarter will bring a new sport into the ecosystem, ensuring constant growth and retention. Ping Pong will follow Badminton, with further releases planned in future quarters.'
      },
      {
        title: 'Expanding Utility of $GAM',
        description: "The $GAM token's utility will be expanded continuously based on community feedback and industry trends. This will include the introduction of exclusive in-app events, enhanced rewards, and more AI-powered features."
      },
      {
        title: 'Commitment to Innovation',
        description: 'We are committed to innovation and user satisfaction, ensuring long-term growth and engagement within the GameMax ecosystem. Regular updates, new sports, and continuous P2E enhancements will keep the platform fresh and engaging.'
      }
    ],
    icon: '/assets/Gamemax-Icons-Future.png'
  }
];

// Calculate overall roadmap progress
const calculateOverallProgress = () => {
  const totalPhases = roadmapData.length;
  let completedValue = 0;
  
  roadmapData.forEach(phase => {
    if (phase.status === 'completed') {
      completedValue += 1;
    } else if (phase.status === 'in-progress') {
      completedValue += (phase.completionPercentage / 100);
    }
  });
  
  return (completedValue / totalPhases) * 100;
};

const Roadmap = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const timelineRef = useRef(null);
  const progressRef = useRef(null);
  const phaseRefs = useRef([]);
  const itemRefs = useRef([]);
  const [openPhaseId, setOpenPhaseId] = useState(null);
  
  // Reset refs for each render
  phaseRefs.current = [];
  itemRefs.current = [];
  
  // Toggle function for accordion
  const handleTogglePhase = (phaseId) => {
    setOpenPhaseId(prevId => (prevId === phaseId ? null : phaseId));
    // Refresh ScrollTrigger after state change to adjust for layout changes
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        ScrollTrigger.refresh();
      }
    }, 600); // Delay matches CSS transition + small buffer
  };
  
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    
    const section = sectionRef.current;
    const title = titleRef.current;
    const timeline = timelineRef.current;
    const progress = progressRef.current;
    
    if (!section || !title || !timeline || !progress) return;
    
    // Split text for title animation
    const splitTitle = new SplitType(title, { types: 'chars, words' });
    
    // Main timeline animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top bottom-=10%',
        end: 'bottom bottom',
        toggleActions: 'play none none reverse',
      }
    });
    
    // Define animation timings
    const titleDuration = 0.4;
    const timelineDuration = 0.7;
    const progressDuration = 0.7;
    const phaseDuration = 0.35;
    const initialPhaseDelay = 0.1; // Delay after progress bar finishes
    const phaseStagger = 0.1; // Stagger between phases starting
    const titleCharStagger = 0.01;
    
    // Animate title
    tl.fromTo(splitTitle.chars, 
      { y: 50, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        stagger: titleCharStagger, 
        duration: titleDuration, 
        ease: 'power3.out' 
      }
    );
    
    // Animate timeline 
    // Starts when title starts (offset equals title duration)
    tl.fromTo(timeline, 
      { scaleY: 0, transformOrigin: 'top' },
      { 
        scaleY: 1, 
        duration: timelineDuration, 
        ease: 'power2.inOut' 
      },
      `-=${titleDuration}` 
    );
    
    // Progress indicator animation
    // Starts at the same time as the timeline animation
    tl.fromTo(progress,
      { height: 0, opacity: 0 },
      {
        height: `${calculateOverallProgress()}%`,
        opacity: 1,
        duration: progressDuration,
        ease: 'power2.inOut'
      },
      "<" // Position parameter: starts concurrently with the previous animation (timeline)
    );

    // Calculate when the initial elements (title, timeline, progress) finish animating
    const timelineEndTime = Math.max(titleDuration, timelineDuration); 
    
    // Animate each phase
    phaseRefs.current.forEach((phase, idx) => {
      // Calculate absolute start time for each phase
      const startTime = timelineEndTime + initialPhaseDelay + (idx * phaseStagger);
      
      tl.fromTo(phase,
        { x: -50, opacity: 0 },
        { 
          x: 0, 
          opacity: 1, 
          duration: phaseDuration, 
          ease: 'power2.out' 
        },
        startTime // Use absolute start time as position parameter
      );
    });
    
    // Animate items for each phase with staggered reveals
    itemRefs.current.forEach((items, phaseIdx) => {
      if (items && items.length > 0) {
        items.forEach((item, itemIdx) => {
          if (item) {
            ScrollTrigger.create({
              trigger: item,
              start: 'top bottom-=5%',
              toggleClass: { targets: item, className: styles.itemVisible },
              once: true,
            });
          }
        });
      }
    });
    
    // Clean up
    return () => {
      if (tl) tl.kill();
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);
  
  // Add to phase refs
  const addToPhaseRefs = (el) => {
    if (el && !phaseRefs.current.includes(el)) {
      phaseRefs.current.push(el);
    }
  };
  
  // Add to item refs (organized by phase)
  const addToItemRefs = (el, phaseIdx, itemIdx) => {
    if (!itemRefs.current[phaseIdx]) {
      itemRefs.current[phaseIdx] = [];
    }
    
    if (el && !itemRefs.current[phaseIdx].includes(el)) {
      if (itemRefs.current[phaseIdx][itemIdx]) {
        itemRefs.current[phaseIdx][itemIdx] = el;
      } else {
        itemRefs.current[phaseIdx].push(el);
      }
    }
  };
  
  const getStatusClass = (status) => {
    switch(status) {
      case 'completed':
        return styles.completed;
      case 'in-progress':
        return styles.inProgress;
      default:
        return styles.upcoming;
    }
  };
  
  return (
    <section className={styles.roadmap} ref={sectionRef}>
      <div className={styles.container}>
        <div className={styles.roadmapHeader}>
          <h2 ref={titleRef}>Our Roadmap</h2>
          <p>Our journey to revolutionize racket sports</p>
          
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressIndicator} 
                style={{ width: `${calculateOverallProgress()}%` }}
              >
                <span className={styles.progressValue}>{Math.round(calculateOverallProgress())}% Complete</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.roadmapContent}>
          <div className={styles.timelineTrack} ref={timelineRef}>
            <div className={styles.timelineProgress} ref={progressRef}></div>
          </div>
          
          {roadmapData.map((phase, phaseIdx) => {
            const isOpen = openPhaseId === phase.id;
            return (
              <div 
                key={phase.id} 
                className={`${styles.phase} ${getStatusClass(phase.status)} ${isOpen ? styles.phaseOpen : ''}`}
                ref={(el) => addToPhaseRefs(el)}
              >
                <div className={styles.phaseIcon}>
                  <div className={`${styles.iconCircle} ${getStatusClass(phase.status)}`}>
                    <img src={phase.icon} alt={phase.title} />
                    {phase.status === 'in-progress' && (
                      <div 
                        className={styles.circleProgress} 
                        style={{ 
                          clipPath: `polygon(0 0, 100% 0, 100% ${phase.completionPercentage}%, 0 ${phase.completionPercentage}%)` 
                        }}
                      ></div>
                    )}
                  </div>
                  {phaseIdx < roadmapData.length - 1 && (
                    <div className={`${styles.connectorLine} ${getStatusClass(phase.status)}`}></div>
                  )}
                </div>
                
                <div className={styles.phaseContent}>
                  <div className={styles.phaseHeader} onClick={() => handleTogglePhase(phase.id)}>
                    <h3 className={styles.phaseTitle}>{phase.title}</h3>
                    <div className={styles.phaseStatus}>
                      {phase.status === 'completed' && <span>Completed</span>}
                      {phase.status === 'in-progress' && <span>{phase.completionPercentage}% Complete</span>}
                      {phase.status === 'upcoming' && <span>Upcoming</span>}
                    </div>
                    <span className={styles.accordionToggle}>{isOpen ? '-' : '+'}</span>
                  </div>
                  
                  <div className={`${styles.phaseItemsContainer} ${isOpen ? styles.open : ''}`}>
                    <div className={styles.phaseItems}>
                      {phase.items.map((item, itemIdx) => (
                        <div 
                          key={`${phase.id}-item-${itemIdx}`} 
                          className={`${styles.item} ${getStatusClass(phase.status)}`}
                          ref={(el) => addToItemRefs(el, phaseIdx, itemIdx)}
                        >
                          <h4 className={styles.itemTitle}>{item.title}</h4>
                          <p className={styles.itemDescription}>{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Roadmap; 
